import { Chip, Stack, Tooltip } from "@mui/material";
import { Box } from "@mui/system";
import { chain, omit } from "lodash-es";
import { useState } from "react";

import PageTabsV2 from "@/App/Layouts/PageBody/PageTabsV2";
import AppIconButton from "@/common/components/Button/AppIconButton";
import DataTabular from "@/common/components/DataTabular/DataTabular";
import Datetime from "@/common/components/Datetime/Datetime";
import InlineUser from "@/common/components/Entity/User/InlineUser";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import BooleanValue from "@/common/components/Form/Display/BooleanValue";
import AppIcon from "@/common/components/Icons/AppIcon";
import TableCellContentOfTags from "@/common/components/Table/TableCell/TableCellContentOfTags";
import AppTooltip from "@/common/components/Tooltip/AppTooltip";
import { ROUTE_PATH } from "@/common/constants/routing";
import { FilterCatalog } from "@/common/filters/filterCatalog";
import { TagStaticFilterInputDto } from "@/common/helpers/entity/tags";
import { TenantRequestHelper } from "@/common/helpers/entity/tenantRequest";
import { getTypedPath } from "@/common/helpers/typedPath";
import { UseApiRequestResult } from "@/common/hooks/api/useApiRequest";
import { UseCommonRequestParamsHookResult } from "@/common/hooks/api/useCommonRequestParams";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { UseCommonViewParamsHookResult } from "@/common/hooks/layout/useCommonViewParams";
import { TabDefinition } from "@/common/hooks/layout/usePageTabs";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { cast } from "@/common/ts/conversions";
import { FilterFieldType } from "@/common/ts/filters";
import { ViewVariant } from "@/common/ts/layout";
import {
  PaginationDtoOfTenantRequestDto,
  TagEntityType,
  TenantRequestCountersDto,
  TenantRequestDto,
  TenantRequestGetPaginatedDto,
  TenantRequestStage,
} from "@/core/api/generated/v1";

import ContractLink from "../../Contract/ContractLink";
import DamageDetectionLink from "../../DamageDetection/DamageDetectionLink";
import TenantInline from "../../Tenant/TenantInline";
import TenantRequestMenu, { TenantRequestMenuDisplayProps } from "../TenantRequestMenu";
import TenantRequestStagePrimaryActions from "../TenantRequestStagePrimaryActions";
import TenantRequestViewModal from "../View/TenantRequestViewModal";

export const TenantRequestStageForTabs = {
  ...omit(TenantRequestStage, [TenantRequestStage.None]),
} as const;
export type TenantRequestStageForTabs =
  (typeof TenantRequestStageForTabs)[keyof typeof TenantRequestStageForTabs];

export const TenantRequestTabularTabs = {
  All: "All",
  ...TenantRequestStageForTabs,
} as const;
export type TenantRequestTabularTabs =
  (typeof TenantRequestTabularTabs)[keyof typeof TenantRequestTabularTabs];

const defaultTabVisibleTabs: TenantRequestTabularTabs[] = [
  TenantRequestTabularTabs.All,
  TenantRequestTabularTabs.Draft,
  TenantRequestTabularTabs.Pending,
  TenantRequestTabularTabs.Declined,
  TenantRequestTabularTabs.Reporting,
  TenantRequestTabularTabs.Returned,
];
const defaultTabVisibilityMap = chain(Object.values(TenantRequestTabularTabs))
  .keyBy((x) => x)
  .mapValues((x) => defaultTabVisibleTabs.includes(x))
  .value() as Record<TenantRequestTabularTabs, boolean>;

const defaultDisplayProps = {
  tabs: true,
  counters: true,
  filters: true,
  sort: true,
  rowActions: {
    inline: {
      viewInModal: false,
      stagePrimary: true,
    },
    menu: cast<TenantRequestMenuDisplayProps>({ edit: true, editTags: true, delete: true }),
  },
  viewVariant: ViewVariant.Page,
};

export type TenantRequestGetPaginatedInputDto = Omit<TenantRequestGetPaginatedDto, "tags"> & {
  tags?: TagStaticFilterInputDto;
};

export interface TenantRequestPaginatedListOwnProps {
  commonViewParams: Nil<UseCommonViewParamsHookResult>;
  commonRequestParams: UseCommonRequestParamsHookResult<TenantRequestGetPaginatedInputDto>;
  paginatedTenantRequestsRequest:
    | UseApiRequestResult<any, any, any, any, PaginationDtoOfTenantRequestDto>
    | null
    | undefined;
  paginatedTenantRequests: PaginationDtoOfTenantRequestDto | null | undefined;
  counters?: TenantRequestCountersDto | null;
  displayProps?: Partial<typeof defaultDisplayProps>;
}

export type TenantRequestPaginatedListProps = TenantRequestPaginatedListOwnProps;

export default function TenantRequestTabular({
  commonViewParams,
  commonRequestParams,
  paginatedTenantRequestsRequest,
  paginatedTenantRequests,
  counters,
  displayProps,
}: TenantRequestPaginatedListProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const currentTenant = useCurrentTenant();

  const [entityToView, setEntityToView] = useState<TenantRequestDto | undefined>(undefined);

  const isCountersVisible = displayProps?.counters && !commonRequestParams.isAnyFilters;

  return (
    <Box sx={{ borderRadius: "inherit" }}>
      <DataTabular<TenantRequestDto>
        tabs={
          displayProps?.tabs && commonViewParams
            ? {
                custom: (params) => (
                  <PageTabsV2
                    tabIdsDefinition={TenantRequestTabularTabs}
                    defaultTabId={TenantRequestTabularTabs.All}
                    variant='tabular'
                    viewVariant={displayProps?.viewVariant}
                    commonViewParams={commonViewParams}
                    tabsProps={params.tabsProps}
                    tabProps={params.tabProps}
                    tabs={[
                      {
                        value: TenantRequestTabularTabs.All,
                        isVisible: defaultTabVisibilityMap[TenantRequestTabularTabs.All],
                        isHideable: false,
                        label: (
                          <Box>
                            {TenantRequestTabularTabs.All}
                            {isCountersVisible && counters && (
                              <Tooltip title='Total' enterDelay={300} enterNextDelay={300}>
                                <Chip
                                  sx={{ ml: 1 }}
                                  size='small'
                                  color='default'
                                  variant='outlined'
                                  label={counters.totalCount ?? "..."}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        ),
                      },
                      ...enumService
                        .getEnumValues(ApiEnumName.TenantRequestStage, { exceptNone: true })
                        .map(
                          (stage, i) =>
                            ({
                              value: stage as TenantRequestTabularTabs,
                              isVisible:
                                defaultTabVisibilityMap[stage as TenantRequestTabularTabs] ?? false,
                              isHideable: true,
                              label: (
                                <Box key={`${i}}.${stage}`}>
                                  {enumService.getEnumValueName(
                                    ApiEnumName.TenantRequestStage,
                                    stage,
                                  )}
                                  {isCountersVisible && counters && (
                                    <AppTooltip
                                      title={enumService.getEnumValueName(
                                        ApiEnumName.TenantRequestStage,
                                        stage,
                                      )}
                                      enterDelay={300}
                                      enterNextDelay={300}
                                    >
                                      <Chip
                                        sx={{ ml: 1 }}
                                        size='small'
                                        color='default'
                                        variant='outlined'
                                        label={counters.countByStageMap?.[stage] ?? "..."}
                                      />
                                    </AppTooltip>
                                  )}
                                </Box>
                              ),
                            }) satisfies TabDefinition<TenantRequestTabularTabs>,
                        ),
                    ]}
                  >
                    {null}
                  </PageTabsV2>
                ),
              }
            : undefined
        }
        columns={[
          // TabularHelper.getColumnForEntityChips<TenantRequestDto>({
          //   chipListProps: {
          //     displayProps: {
          //       unseenInfo: false, // as chat activator shows unread info, so disable  to avoid sync issues
          //     },
          //   },
          // }),
          {
            field: getTypedPath<TenantRequestDto>().id.$path,
            title: "ID",
            width: 250,
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => item.id,
            filters: {
              fieldType: FilterFieldType.Id,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().globalNumber.$path,
            title: "Number",
            width: 150,
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => item.globalNumber,
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().externalNumber.$path,
            title: "Ext. number",
            description: "External number",
            width: 100,
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              TenantRequestHelper.isSentByMe(currentTenant, item)
                ? item.externalNumber || "-"
                : "-",
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().name.$path,
            title: "Name",
            width: 250,
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => item.name || "-",
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().description.$path,
            title: "Description",
            width: 100,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) => item.description || "-",
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().content.type.$path,
            title: "Type",
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => (
              <InlineApiEnumValue type='TenantRequestType' value={item.content?.type} />
            ),
            filters: {
              fieldType: FilterFieldType.Enum,
              isUseDefaultOperators: true,
              fieldTypeMeta: {
                enum: {
                  enumName: ApiEnumName.TenantRequestType,
                },
              },
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().content.operation.operationType.$path,
            title: "Operation type",
            width: 200,
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.content?.operation ? (
                <InlineApiEnumValue
                  type='TenantRequestOperationType'
                  value={item.content.operation?.operationType}
                />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Enum,
              isUseDefaultOperators: true,
              fieldTypeMeta: {
                enum: {
                  enumName: ApiEnumName.TenantRequestOperationType,
                },
              },
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().stageHistory.stage.$path,
            title: "Stage",
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => (
              <InlineApiEnumValue
                type={ApiEnumName.TenantRequestStage}
                value={item.stageHistory?.stage}
                withHelperTooltip
              />
            ),
            filters: {
              fieldType: FilterFieldType.Enum,
              isUseDefaultOperators: true,
              fieldTypeMeta: {
                enum: {
                  enumName: ApiEnumName.TenantRequestStage,
                },
              },
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().appliedResult.status.$path,
            title: "Applied",
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.appliedResult?.status && TenantRequestHelper.isSentByMe(currentTenant, item) ? (
                <InlineApiEnumValue
                  type={ApiEnumName.ApplyStatus}
                  value={item.appliedResult?.status}
                  withHelperTooltip
                />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Enum,
              isUseDefaultOperators: true,
              fieldTypeMeta: {
                enum: {
                  enumName: ApiEnumName.ApplyStatus,
                },
              },
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().isOpen.$path,
            title: "Is open",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => <BooleanValue value={item.isOpen} />,
            filters: {
              fieldType: FilterFieldType.Boolean,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().senderTenant.id.$path,
            title: "Sender",
            width: 250,
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => <TenantInline entity={item.senderTenant} />,
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfTenant(),
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().receiverTenant.id.$path,
            title: "Receiver",
            width: 250,
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => <TenantInline entity={item.receiverTenant} />,
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfTenant(),
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().billTo.name.$path,
            title: "Bill to",
            width: 100,
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => item.billTo?.name || "-",
            filters: {
              fieldType: FilterFieldType.String,
              operators: FilterCatalog.getOperatorsForStringField(),
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().lastActivityAt.$path,
            title: "Last activity",
            width: 200,
            isVisible: true,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) => (
              <Datetime datetime={item.lastActivityAt} direction='column' withDurationFromNow />
            ),
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().responseType.$path,
            title: "Response",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.responseType ? (
                <InlineApiEnumValue
                  type={ApiEnumName.TenantRequestResponseType}
                  value={item.responseType}
                  withHelperTooltip
                />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Enum,
              isUseDefaultOperators: true,
              fieldTypeMeta: {
                enum: {
                  enumName: ApiEnumName.TenantRequestResponseType,
                },
              },
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().respondedAt.$path,
            title: "Responded at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.respondedAt ? <Datetime datetime={item.respondedAt} withDurationFromNow /> : "-",
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().responseReason.$path,
            title: "Response reason",
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) => item.responseReason || "-",
            filters: {
              fieldType: FilterFieldType.String,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().contract.id.$path,
            title: "Contract",
            width: 150,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            isToDisabled: true,
            renderCell: (item) =>
              item.contract && TenantRequestHelper.isSentByMe(currentTenant, item) ? (
                <ContractLink entityId={undefined} entity={item.contract} />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfContract(),
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().damageDetection.id.$path,
            title: "Damage detection",
            width: 150,
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            isToDisabled: true,
            renderCell: (item) =>
              item.contract && TenantRequestHelper.isSentByMe(currentTenant, item) ? (
                <DamageDetectionLink entityId={undefined} entity={item.damageDetection} />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfDamageDetection(),
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().createdAt.$path,
            title: "Created at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.createdAt ? <Datetime datetime={item.createdAt} withDurationFromNow /> : "-",
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().updatedAt.$path,
            title: "Updated at",
            isVisible: false,
            isHideable: true,
            isSortable: true,
            isFilterable: true,
            renderCell: (item) =>
              item.updatedAt ? <Datetime datetime={item.updatedAt} withDurationFromNow /> : "-",
            filters: {
              fieldType: FilterFieldType.Date,
              isUseDefaultOperators: true,
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().createdBy.$path,
            title: "Created by",
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) =>
              item.createdBy && TenantRequestHelper.isSentByMe(currentTenant, item) ? (
                <InlineUser userId={item.createdBy} withAvatar />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfUser(),
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().updatedBy.$path,
            title: "Updated by",
            isVisible: false,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            renderCell: (item) =>
              item.updatedBy && TenantRequestHelper.isSentByMe(currentTenant, item) ? (
                <InlineUser userId={item.updatedBy} withAvatar />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Id,
              operators: FilterCatalog.getOperatorsForIdFieldOfUser(),
            },
          },
          {
            field: getTypedPath<TenantRequestDto>().tags.$path,
            title: "Tags",
            flex: 0,
            isVisible: true,
            isHideable: true,
            isSortable: false,
            isFilterable: true,
            isColumnMenuDisabled: false,
            isToDisabled: true,
            renderCell: (item) =>
              TenantRequestHelper.isSentOrReceivedByMe(currentTenant, item) ? (
                <TableCellContentOfTags
                  entityType={TagEntityType.TenantRequest}
                  entity={item}
                  onUpdate={(newValue) => {
                    paginatedTenantRequestsRequest?.updateData((data) => {
                      data.items?.forEach((item2) => {
                        if (item2.id === item.id) {
                          item2.tags = newValue || undefined;
                        }
                      });
                    });
                  }}
                />
              ) : (
                "-"
              ),
            filters: {
              fieldType: FilterFieldType.Arbitrary,
              isUseSingleFilter: true,
              operators: FilterCatalog.getOperatorsForTagsField(),
            },
          },
        ]}
        rows={paginatedTenantRequests?.items}
        isLoading={paginatedTenantRequestsRequest?.isLoading}
        getRowId={(item) => item.id!}
        rowTo={(item) => ROUTE_PATH.TENANT_REQUEST_VIEW(item.id)}
        rowActions={(() => {
          const approxInlineActionCount =
            (displayProps?.rowActions?.inline?.viewInModal ? 1 : 0) +
            (displayProps?.rowActions?.inline?.stagePrimary ? 3 : 0) +
            (displayProps?.rowActions?.menu ? 1 : 0);

          return {
            variant: "arbitrary",
            columnProps: {
              minWidth: Math.ceil(approxInlineActionCount * 50),
              flex: undefined,
            },
            renderActions: ({ item }) =>
              displayProps?.rowActions ? (
                <Stack direction='row' spacing={0.5} justifyContent='flex-end' alignItems='center'>
                  {displayProps?.rowActions?.inline?.viewInModal && (
                    <AppIconButton
                      variant='text'
                      color='default'
                      size='small'
                      tooltipProps={{ title: "Quick view" }}
                      onClick={() => {
                        setEntityToView(item);
                      }}
                    >
                      <AppIcon of='quickView' />
                    </AppIconButton>
                  )}

                  {displayProps?.rowActions?.inline?.stagePrimary && (
                    <TenantRequestStagePrimaryActions
                      variant='compact'
                      tenantRequest={item}
                      onUpdate={(newValue) => {
                        paginatedTenantRequestsRequest?.updateData((data) => {
                          data.items = data.items?.map((x) =>
                            x.id === newValue.id ? newValue : x,
                          );
                        });
                      }}
                    />
                  )}

                  {displayProps?.rowActions?.menu && (
                    <TenantRequestMenu
                      entity={item}
                      onDelete={() => paginatedTenantRequestsRequest?.refetch()}
                      onUpdate={() => paginatedTenantRequestsRequest?.refetch()}
                      displayProps={{
                        ...displayProps?.rowActions?.menu,
                      }}
                    />
                  )}
                </Stack>
              ) : undefined,
          };
        })()}
        statePersistence={commonRequestParams.dataTabularProps.statePersistence}
        pagination={commonRequestParams.dataTabularProps.pagination}
        sort={displayProps?.sort ? commonRequestParams.dataTabularProps.sort : undefined}
        quickFilter={
          displayProps?.filters ? commonRequestParams.dataTabularProps.quickFilter : undefined
        }
        filters={displayProps?.filters ? commonRequestParams.dataTabularProps.filters : undefined}
        refetch={commonRequestParams.dataTabularProps.refetch}
      />

      {/* Delete */}
      {entityToView && (
        <TenantRequestViewModal
          open={!!entityToView}
          onClose={() => setEntityToView(undefined)}
          viewProps={{
            tenantRequestId: entityToView.id,
            tenantRequest: entityToView,
            onUpdate: () => {
              paginatedTenantRequestsRequest?.refetch();
            },
            onDelete: () => {
              paginatedTenantRequestsRequest?.refetch();
              setEntityToView(undefined);
            },
          }}
        />
      )}
    </Box>
  );
}
